import { Component, inject, OnInit, output, signal, ChangeDetectionStrategy } from '@angular/core';
import { email, FormField, form, pattern, required, maxLength } from '@angular/forms/signals';
import { ETipoPermiso } from '@core/enums/tipoPermiso.enum';
import { EmpleadoApiService } from '@core/services/api/empleado-api.service';
import { ToastService } from '@shared/services/toast.service';
import { SucursalService } from '@shared/services/sucursal.service';
import { UiButton, UiInputNumber, UiPassword, UiSelect, UiTooltip } from '@shared/components/ui';
import { FormErrorMessagePipe } from '../../pipes/form-error-message.pipe';
import { GrupoPermisoService } from '@shared/services/grupo-permiso.service';
import { environment } from '@env/environment';
import { IEmpleadoData } from '@core/interfaces/entidades/empleado.entidad';
import { ICrearEmpleadoReq } from '@core/interfaces/request-response/empleado.dto.interface';
import { IOptionsSelect } from '@core/interfaces/options.select.interface';

@Component({
  selector: 'app-crear-empleado',
  imports: [
    FormField,
    UiInputNumber,
    UiPassword,
    UiButton,
    UiSelect,
    UiTooltip,
    FormErrorMessagePipe,
  ],
  templateUrl: './crear-empleado.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './crear-empleado.css',
})
export class CrearEmpleado implements OnInit {
  ngOnInit(): void {
    this.opcionesSucursal.set([
      { label: 'Todas las sucursales', value: null },
      ...this.sucursalService.sucursales().map((s) => ({ label: s.nombre, value: s.id })),
    ]);
  }

  sucursalService = inject(SucursalService);
  opcionesSucursal = signal<IOptionsSelect<number | null>[]>([]);
  // services
  empleadoApiService = inject(EmpleadoApiService);
  grupoPermisoService = inject(GrupoPermisoService);
  toastService = inject(ToastService);

  empleadoForm = form(
    signal({
      nombre_completo: '',
      email: '',
      telefono: '',
      contra: '',
      sucursal_id: null as number | null,
      permisos: [] as ETipoPermiso[],
    }),
    (schemaPath) => {
      required(schemaPath.nombre_completo, { message: 'El nombre completo es obligatorio.' });
      required(schemaPath.email, { message: 'El email es obligatorio.' });
      email(schemaPath.email, { message: 'El email no es válido.' });
      // required(schemaPath.telefono, { message: 'El teléfono es obligatorio.' });
      required(schemaPath.contra, { message: 'La contraseña es obligatoria.' });
      pattern(schemaPath.contra, environment.strongPasswordRegex, {
        message:
          'La contraseña debe tener al menos 8 caracteres, incluyendo una letra mayúscula, una letra minúscula y un número.',
      });

      pattern(schemaPath.nombre_completo, /^[a-zA-ZáéíóúÁÉÍÓÚüÜñÑ\s]+$/, {
        message: 'El nombre solo puede contener letras y espacios.',
      });
      maxLength(schemaPath.nombre_completo, 50, {
        message: 'El nombre no puede exceder los 50 caracteres.',
      });
    },
  );
  // evento emit
  cerrar = output<IEmpleadoData | undefined>();
  onCerrar(data?: IEmpleadoData) {
    this.cerrar.emit(data);
  }
  // funciones
  limpiarPermisos() {
    this.empleadoForm().value.set({
      ...this.empleadoForm().controlValue(),
      permisos: [],
    });
  }
  crear($event: Event) {
    $event.preventDefault();
    if (this.empleadoForm().invalid()) {
      this.empleadoForm().markAsTouched();
      this.toastService.show(
        'top-right',
        'warn',
        'Formulario inválido',
        'Por favor, revise los campos.',
      );
      return;
    }
    const req: ICrearEmpleadoReq = {
      nombre_completo: this.empleadoForm().controlValue().nombre_completo,
      email: this.empleadoForm().controlValue().email,
      contra: this.empleadoForm().controlValue().contra,
      permisos: this.empleadoForm().controlValue().permisos,
      empresa_id: undefined,
      sucursal_id: this.empleadoForm().controlValue().sucursal_id,
      telefono: this.empleadoForm().controlValue().telefono.toString(),
    };
    this.empleadoApiService.crear(req).subscribe({
      next: (empleadoData: IEmpleadoData) => {
        this.toastService.show(
          'top-right',
          'success',
          'Empleado creado',
          'El empleado ha sido creado con éxito.',
        );
        this.onCerrar(empleadoData);
      },
      error: (err) => {
        if (err.status == 401) return;
        this.toastService.show(
          'top-right',
          'error',
          'Error',
          err.error.message || 'Error al crear el empleado.',
        );
      },
    });
  }
  onTogglePermiso(permiso: ETipoPermiso, event: any) {
    if (event.target.checked) {
      const permisosSeleccionados = this.empleadoForm().controlValue().permisos;
      if (!permisosSeleccionados.includes(permiso)) {
        permisosSeleccionados.push(permiso);
      }
    } else {
      this.empleadoForm().value.set({
        ...this.empleadoForm().controlValue(),
        permisos: this.empleadoForm()
          .controlValue()
          .permisos.filter((p) => p !== permiso),
      });
    }
  }
}